<template>
    <div class="eod-spread-tiers">
        <div class="subtitle-2 pb-2">
            {{ $t('settings.eod_spread_tiers') }}
        </div>
        <div
            v-for="(tier, index) in tiers"
            :key="rowsVersion + '-' + index"
            class="pb-4"
        >
            {{ $t('settings.eod_spread_tier_text_1') }}
            <currency-input
                v-validate.immediate="'required|min_value:0'"
                class="currency-input-field"
                :value="tier.minOrderValue"
                :name="getName(index, 'minOrderValue')"
                :error-messages="thresholdErrors(index)"
                :data-vv-as="$t('settings.eod_spread_threshold')"
                @input="updateTier(index, 'minOrderValue', $event)"
            ></currency-input>
            {{ $t('settings.eod_spread_tier_text_2') }}
            <text-input
                v-validate.immediate="
                    'required|integer|min_value:0|max_value:240'
                "
                class="input-field"
                type="number"
                :value="tier.spread"
                :name="getName(index, 'spread')"
                :error-messages="errors.collect(getName(index, 'spread'))"
                :data-vv-as="$t('settings.eod_spread_value')"
                @input="updateTier(index, 'spread', toNumber($event))"
            ></text-input>
            {{ $t('settings.eod_spread_tier_text_3') }}
            <v-btn icon color="warning" @click="deleteTier(index)">
                <v-icon>delete</v-icon>
            </v-btn>
        </div>
        <v-input :error-messages="zeroRowErrors" hide-details="auto"></v-input>
        <v-btn class="ma-2" outlined color="success" @click="addTier">
            <v-icon left>add</v-icon> {{ $t('actions.add') }}
        </v-btn>
    </div>
</template>

<style scoped>
.input-field {
    display: inline-block;
    width: 80px;
    margin-right: 10px;
    margin-left: 10px;
}
.currency-input-field {
    display: inline-block;
    width: 110px;
    margin-right: 10px;
    margin-left: 10px;
}
</style>

<script>
import TextInput from '@/components/form/Inputs/TextInput'
import CurrencyInput from '@/components/form/Inputs/CurrencyInput'
import CloneDeep from 'lodash/cloneDeep'

const toPence = (value) =>
    value === null || value === '' || value === undefined
        ? null
        : Math.round(Number(value) * 100)

export default {
    components: {
        TextInput,
        CurrencyInput,
    },
    inject: {
        $validator: 'formValidator',
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        name: {
            type: String,
            default: 'eodSpreadTiers',
        },
    },
    data() {
        return {
            // bumped on delete: the rows remount and revalidate (.immediate) under their new index-based names
            rowsVersion: 0,
        }
    },
    computed: {
        tiers() {
            return this.value || []
        },
        duplicateIndexes() {
            const seen = {}
            const duplicates = []
            this.tiers.forEach((tier, index) => {
                const pence = toPence(tier.minOrderValue)
                if (pence === null) return
                if (pence in seen) duplicates.push(index)
                seen[pence] = true
            })
            return duplicates
        },
        zeroRowErrors() {
            return this.tiers.some((tier) => toPence(tier.minOrderValue) === 0)
                ? []
                : [this.$t('settings.eod_spread_zero_row_required')]
        },
    },
    methods: {
        updateTier(index, field, value) {
            const tiers = CloneDeep(this.tiers)
            tiers[index][field] = value
            this.$emit('input', tiers)
        },
        deleteTier(index) {
            const tiers = CloneDeep(this.tiers)
            tiers.splice(index, 1)
            this.rowsVersion++
            this.$emit('input', tiers)
        },
        addTier() {
            const tiers = CloneDeep(this.tiers)
            tiers.push({ minOrderValue: null, spread: null })
            this.$emit('input', tiers)
        },
        getName(index, field) {
            return this.name + '_' + index + '_' + field
        },
        toNumber(value) {
            return value === '' || value === null ? null : Number(value)
        },
        thresholdErrors(index) {
            const errors = this.errors.collect(
                this.getName(index, 'minOrderValue')
            )
            return this.duplicateIndexes.includes(index)
                ? errors.concat(this.$t('settings.eod_spread_duplicate'))
                : errors
        },
    },
}
</script>
